import React, { useEffect, useRef, useState } from "react";
import api from "../../services/api";
import { formatDateWithoutHour } from "../../utils/date";
import { formatCurrencyFromDb, maskCpfCnpj } from "../../utils/masks";

// Período padrão: notas emitidas até 120 dias antes do boleto e até o
// vencimento (há fornecedor que emite o boleto dias antes da nota).
const DIAS_ANTES = 120;

const somarDias = (dataIso, dias) => {
    if (!dataIso) return "";
    const data = new Date(`${String(dataIso).split('T')[0]}T00:00:00Z`);
    data.setUTCDate(data.getUTCDate() + dias);
    return data.toISOString().slice(0, 10);
};

const formatarRaiz = (doc) => (doc.length === 8 ? `${doc.slice(0, 2)}.${doc.slice(2, 5)}.${doc.slice(5, 8)}` : maskCpfCnpj(doc));

const descreverIndicio = (indicio) => {
    if (indicio === 'parcela-xml') return { texto: 'Parcela da nota confere (vencimento e valor)', classe: 'bg-emerald-100 text-emerald-900' };
    if (indicio === 'valor-igual') return { texto: 'Valor igual ao do boleto', classe: 'bg-emerald-100 text-emerald-900' };
    if (indicio === 'soma-grupo') return { texto: 'Somada com outras notas dá o valor do boleto', classe: 'bg-violet-100 text-violet-900' };
    if (indicio.startsWith('multiplo:')) return { texto: `Nota de ${indicio.split(':')[1]}× o valor do boleto (parcelada)`, classe: 'bg-sky-100 text-sky-900' };
    if (indicio.startsWith('valor-proximo:')) {
        const diferenca = Number(indicio.split(':')[1]);
        return { texto: `Valor próximo (boleto ${diferenca > 0 ? 'maior' : 'menor'} em ${formatCurrencyFromDb(Math.abs(diferenca))})`, classe: 'bg-amber-100 text-amber-900' };
    }
    return { texto: indicio, classe: 'bg-gray-100 text-gray-800' };
};

/**
 * Escolha manual da nota de um boleto sem obra, a partir do Relatório de Boletos.
 * A busca é feita pelo backend (GET /boletos/:id/notas-candidatas) e o vínculo
 * usa o mesmo endpoint da tela de Boletos, que marca a escolha como manual.
 */
const ModalVincularNotaBoleto = ({ boleto, onFechar, onVinculado, onDesvinculado }) => {
    const [filtros, setFiltros] = useState({
        escopo: 'fornecedor',
        tipo: 'todos',
        busca: '',
        dataInicio: somarDias(boleto.dataEmissao || boleto.dataVencimento, -DIAS_ANTES),
        dataFim: somarDias(boleto.dataVencimento || boleto.dataEmissao, 0),
    });
    const [buscaDigitada, setBuscaDigitada] = useState('');
    const [resultado, setResultado] = useState({ records: [], total: 0, limitado: false, documentos: [], grupoSoma: null, temValorCompativel: true });
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState('');
    const [selecionada, setSelecionada] = useState(null);
    const [vinculando, setVinculando] = useState(false);
    // Remover vínculo pede confirmação: o primeiro clique só arma o botão
    const [confirmarRemocao, setConfirmarRemocao] = useState(false);
    const sequencia = useRef(0);
    const buscaRef = useRef(null);
    const dialogoRef = useRef(null);

    const notaAtual = boleto.notaFiscal;
    // Enquanto a digitação não vira filtro, a lista na tela já não vale
    const aguardandoBusca = buscaDigitada !== filtros.busca;
    const exibindoCarregando = carregando || aguardandoBusca;
    const erroPeriodo = filtros.dataInicio && filtros.dataFim && filtros.dataInicio > filtros.dataFim
        ? 'A data inicial é maior que a final.'
        : '';

    // Busca digitada só vira filtro depois de uma pausa na digitação
    useEffect(() => {
        const t = setTimeout(() => setFiltros((f) => (f.busca === buscaDigitada ? f : { ...f, busca: buscaDigitada })), 400);
        return () => clearTimeout(t);
    }, [buscaDigitada]);

    useEffect(() => {
        if (erroPeriodo) return;
        const id = ++sequencia.current;
        setCarregando(true);
        setErro('');
        const params = new URLSearchParams({ escopo: filtros.escopo, tipo: filtros.tipo, limit: '50' });
        if (filtros.busca.trim()) params.append('busca', filtros.busca.trim());
        if (filtros.dataInicio) params.append('dataInicio', filtros.dataInicio);
        if (filtros.dataFim) params.append('dataFim', filtros.dataFim);

        api.get(`/boletos/${boleto.id}/notas-candidatas?${params.toString()}`)
            .then((res) => {
                if (id !== sequencia.current) return;
                setResultado(res.data);
                // A nota escolhida pode ter saído da lista com o novo filtro
                setSelecionada((sel) => (sel && res.data.records.some((n) => n.id === sel.id && n.tipo === sel.tipo) ? sel : null));
            })
            .catch((error) => {
                if (id !== sequencia.current) return;
                setResultado({ records: [], total: 0, limitado: false, documentos: [], grupoSoma: null, temValorCompativel: true });
                setErro(error.response?.data?.error || 'Não foi possível buscar as notas.');
            })
            .finally(() => { if (id === sequencia.current) setCarregando(false); });
    }, [boleto.id, filtros, erroPeriodo]);

    useEffect(() => {
        buscaRef.current?.focus();
    }, []);

    // Esc fecha; Tab e Shift+Tab circulam só dentro do modal
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape' && !vinculando) {
                onFechar();
                return;
            }
            if (e.key !== 'Tab' || !dialogoRef.current) return;
            const focaveis = [...dialogoRef.current.querySelectorAll('button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])')]
                .filter((el) => !el.disabled && el.offsetParent !== null);
            if (focaveis.length === 0) return;
            const primeiro = focaveis[0];
            const ultimo = focaveis[focaveis.length - 1];
            const dentro = dialogoRef.current.contains(document.activeElement);
            if (e.shiftKey && (document.activeElement === primeiro || !dentro)) {
                e.preventDefault();
                ultimo.focus();
            } else if (!e.shiftKey && (document.activeElement === ultimo || !dentro)) {
                e.preventDefault();
                primeiro.focus();
            }
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onFechar, vinculando]);

    const vincular = async () => {
        if (!selecionada || vinculando) return;
        setVinculando(true);
        setErro('');
        try {
            const payload = selecionada.tipo === 'material'
                ? { tipo: 'material', notaFiscalId: selecionada.id, notaFiscalServicoId: null }
                : { tipo: 'servico', notaFiscalServicoId: selecionada.id, notaFiscalId: null };
            await api.put(`/boletos/${boleto.id}/vincular-nfe`, payload);
            onVinculado(selecionada);
        } catch (error) {
            setErro(error.response?.data?.error || 'Não foi possível vincular a nota.');
            setVinculando(false);
        }
    };

    const removerVinculo = async () => {
        if (vinculando) return;
        if (!confirmarRemocao) {
            setConfirmarRemocao(true);
            return;
        }
        setVinculando(true);
        setErro('');
        try {
            await api.put(`/boletos/${boleto.id}/vincular-nfe`, { tipo: null, notaFiscalId: null, notaFiscalServicoId: null });
            onDesvinculado(notaAtual);
        } catch (error) {
            setErro(error.response?.data?.error || 'Não foi possível remover o vínculo.');
            setVinculando(false);
            setConfirmarRemocao(false);
        }
    };

    const setFiltro = (campo, valor) => setFiltros((f) => ({ ...f, [campo]: valor }));
    const botaoTipo = (valor, rotulo, ativo) => (
        <button
            type="button"
            aria-pressed={filtros.tipo === valor}
            onClick={() => setFiltro('tipo', valor)}
            className={`px-3 py-1.5 rounded text-sm font-medium ${filtros.tipo === valor ? ativo : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
        >
            {rotulo}
        </button>
    );

    const raizes = resultado.documentos || [];

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-2 sm:p-4" onMouseDown={(e) => { if (e.target === e.currentTarget && !vinculando) onFechar(); }}>
            <div ref={dialogoRef} role="dialog" aria-modal="true" aria-labelledby="titulo-vincular-nota" className="bg-white rounded-lg w-full max-w-4xl max-h-[92vh] md:h-[88vh] flex flex-col">
                <div className="px-4 sm:px-6 pt-5 pb-3 flex justify-between items-start gap-4 border-b">
                    <div>
                        <h2 id="titulo-vincular-nota" className="text-lg sm:text-xl font-bold text-gray-800">Vincular nota fiscal ao boleto</h2>
                        <p className="text-sm text-gray-600 mt-1">A obra do boleto passa a ser a obra da nota escolhida.</p>
                    </div>
                    <button type="button" onClick={onFechar} disabled={vinculando} className="p-2 hover:bg-gray-100 rounded disabled:opacity-50" aria-label="Fechar">
                        <img className="w-5 h-5" src="/images/icon/close.png" alt="" />
                    </button>
                </div>

                <div className="px-4 sm:px-6 py-4 flex-1 min-h-0 overflow-y-auto md:flex md:flex-col">
                    <div className="mb-4 p-3 bg-gray-50 rounded-lg grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                        <div><span className="text-gray-500">Beneficiário:</span> <span className="text-gray-800 font-medium">{(boleto.beneficiario || '').toUpperCase()}</span></div>
                        <div><span className="text-gray-500">CNPJ/CPF:</span> <span className="text-gray-800">{boleto.beneficiarioDocumento ? maskCpfCnpj(boleto.beneficiarioDocumento) : '—'}</span></div>
                        <div><span className="text-gray-500">Valor:</span> <span className="text-gray-800 font-medium">{formatCurrencyFromDb(boleto.valor)}</span></div>
                        <div>
                            <span className="text-gray-500">Emissão / vencimento:</span>{' '}
                            <span className="text-gray-800">{boleto.dataEmissao ? formatDateWithoutHour(boleto.dataEmissao) : '—'} / {boleto.dataVencimento ? formatDateWithoutHour(boleto.dataVencimento) : '—'}</span>
                        </div>
                        {notaAtual && (
                            <div className="sm:col-span-2 flex flex-wrap items-center justify-between gap-2 text-amber-900">
                                <span>
                                    Nota atual: {notaAtual.numero} ({notaAtual.tipo}), {boleto.obra ? `obra ${boleto.obra.nomeProjeto.trim().toUpperCase()}` : 'sem obra'}. Ao vincular outra, ela será substituída.
                                </span>
                                <span className="flex items-center gap-2">
                                    {confirmarRemocao && (
                                        <button type="button" className="text-sm text-gray-700 underline" disabled={vinculando} onClick={() => setConfirmarRemocao(false)}>
                                            Manter
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        disabled={vinculando}
                                        onClick={removerVinculo}
                                        className={`px-3 py-1 rounded text-sm ${confirmarRemocao ? 'bg-red-600 text-white hover:bg-red-700' : 'border border-red-300 text-red-700 hover:bg-red-50'} disabled:opacity-50`}
                                    >
                                        {confirmarRemocao ? 'Confirmar remoção do vínculo' : 'Remover vínculo'}
                                    </button>
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="flex flex-col gap-3 mb-3">
                        <label className="flex items-start gap-2 text-sm text-gray-800 cursor-pointer">
                            <input
                                type="checkbox"
                                className="mt-0.5 w-4 h-4"
                                checked={filtros.escopo === 'fornecedor'}
                                onChange={(e) => setFiltro('escopo', e.target.checked ? 'fornecedor' : 'todos')}
                            />
                            <span>
                                Só notas do beneficiário do boleto
                                {raizes.length > 0 && <span className="text-gray-500"> (CNPJ {raizes.map(formatarRaiz).join(' ou ')}, matriz e filiais)</span>}
                            </span>
                        </label>

                        <div className="flex flex-wrap items-end gap-3">
                            <div className="flex gap-2" role="group" aria-label="Tipo de nota">
                                {botaoTipo('todos', 'Todas', 'bg-stone-600 text-white')}
                                {botaoTipo('material', 'Material', 'bg-blue-600 text-white')}
                                {botaoTipo('servico', 'Serviço', 'bg-green-600 text-white')}
                            </div>
                            <div className="flex flex-col gap-1 flex-1 min-w-[220px]">
                                <label htmlFor="busca-nota" className="text-xs font-medium text-gray-700">Número, fornecedor ou valor</label>
                                <input
                                    id="busca-nota"
                                    ref={buscaRef}
                                    type="text"
                                    className="w-full px-3 py-1.5 bg-white rounded-md border border-slate-300 text-sm"
                                    placeholder="Ex.: 1425706 ou 3.880,00"
                                    value={buscaDigitada}
                                    onChange={(e) => setBuscaDigitada(e.target.value)}
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <label htmlFor="nota-data-inicio" className="text-xs font-medium text-gray-700">Emissão de</label>
                                <input id="nota-data-inicio" type="date" className="px-2 py-1.5 bg-white rounded-md border border-slate-300 text-sm" value={filtros.dataInicio} onChange={(e) => setFiltro('dataInicio', e.target.value)} aria-invalid={erroPeriodo ? 'true' : 'false'} />
                            </div>
                            <div className="flex flex-col gap-1">
                                <label htmlFor="nota-data-fim" className="text-xs font-medium text-gray-700">até</label>
                                <input id="nota-data-fim" type="date" className="px-2 py-1.5 bg-white rounded-md border border-slate-300 text-sm" value={filtros.dataFim} onChange={(e) => setFiltro('dataFim', e.target.value)} aria-invalid={erroPeriodo ? 'true' : 'false'} />
                            </div>
                            {(filtros.dataInicio || filtros.dataFim) && (
                                <button type="button" className="text-sm text-blue-700 hover:underline pb-1.5" onClick={() => setFiltros((f) => ({ ...f, dataInicio: '', dataFim: '' }))}>
                                    Qualquer data
                                </button>
                            )}
                        </div>
                        {erroPeriodo && <span role="alert" className="text-red-600 text-sm">{erroPeriodo}</span>}
                    </div>

                    <div className="text-sm font-medium text-gray-700 mb-2">
                        Selecione a nota
                        {!exibindoCarregando && !erroPeriodo && resultado.total > 0 && (
                            <span className="font-normal text-gray-500">
                                {' '}— {resultado.limitado ? `mostrando as ${resultado.records.length} mais prováveis; refine o filtro se não encontrar` : `${resultado.records.length} nota(s)`}
                            </span>
                        )}
                    </div>

                    {erro && <div role="alert" className="mb-2 px-3 py-2 rounded bg-red-50 border border-red-200 text-sm text-red-800">{erro}</div>}

                    {!exibindoCarregando && !erroPeriodo && resultado.grupoSoma && (
                        <div className="mb-2 px-3 py-2 rounded bg-violet-50 border border-violet-200 text-sm text-violet-900">
                            <strong>Este boleto parece pagar {resultado.grupoSoma.numeros.length} notas juntas</strong> ({[...resultado.grupoSoma.numeros].sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true })).join(', ')}), que somam {formatCurrencyFromDb(resultado.grupoSoma.total)}.
                            {' '}O boleto aceita uma nota só: o valor inteiro irá para a obra da nota escolhida.
                        </div>
                    )}
                    {!exibindoCarregando && !erroPeriodo && resultado.records.length > 0 && !resultado.temValorCompativel && (
                        <div className="mb-2 px-3 py-2 rounded bg-amber-50 border border-amber-200 text-sm text-amber-900">
                            Nenhuma nota listada tem valor compatível com o boleto ({formatCurrencyFromDb(boleto.valor)}). A nota certa pode ainda não ter sido importada
                            {filtros.escopo === 'fornecedor' ? ' ou ter sido emitida por outro CNPJ (desmarque a opção "Só notas do beneficiário")' : ''}
                            {(filtros.dataInicio || filtros.dataFim) ? ', ou estar fora do período de emissão' : ''}.
                        </div>
                    )}

                    <div className="border border-gray-200 rounded-lg md:flex-1 md:min-h-[16rem] md:overflow-y-auto" aria-busy={exibindoCarregando}>
                        {erroPeriodo ? (
                            <div className="text-center py-8 text-gray-500 text-sm">Corrija o período de emissão para ver as notas.</div>
                        ) : exibindoCarregando ? (
                            <div className="text-center py-8 text-gray-500 text-sm" role="status">Buscando notas...</div>
                        ) : resultado.records.length === 0 ? (
                            <div className="text-center py-8 px-4 text-gray-500 text-sm">
                                Nenhuma nota encontrada com esses filtros.
                                {filtros.escopo === 'fornecedor' && ' Se a nota foi emitida por outro CNPJ, desmarque "Só notas do beneficiário".'}
                                {(filtros.dataInicio || filtros.dataFim) && ' Você também pode buscar em qualquer data.'}
                            </div>
                        ) : (
                            <ul className="divide-y divide-gray-200" aria-label="Notas encontradas">
                                {resultado.records.map((nota) => {
                                    const ativa = selecionada?.id === nota.id && selecionada?.tipo === nota.tipo;
                                    return (
                                        <li key={`${nota.tipo}-${nota.id}`}>
                                            <button
                                                type="button"
                                                aria-pressed={ativa}
                                                onClick={() => setSelecionada(nota)}
                                                className={`w-full text-left p-3 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 ${ativa ? 'bg-blue-50 border-l-4 border-l-blue-600' : 'border-l-4 border-l-transparent'}`}
                                            >
                                                <div className="flex flex-wrap items-start justify-between gap-2">
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex flex-wrap items-center gap-2 mb-1">
                                                            <span className={`text-xs px-2 py-0.5 rounded font-medium text-white ${nota.tipo === 'material' ? 'bg-blue-600' : 'bg-green-600'}`}>
                                                                {nota.tipo === 'material' ? 'MATERIAL' : 'SERVIÇO'}
                                                            </span>
                                                            <span className="text-sm font-medium text-gray-800">Nota {nota.numero}</span>
                                                            <span className="text-xs text-gray-500">emitida em {nota.dataEmissao ? formatDateWithoutHour(nota.dataEmissao) : '—'}</span>
                                                            {nota.atual && <span className="text-xs px-2 py-0.5 rounded bg-amber-100 text-amber-900">nota atual</span>}
                                                        </div>
                                                        <div className="text-sm text-gray-800">
                                                            {nota.obra
                                                                ? <>Obra: <span className="font-medium">{nota.obra.nomeProjeto.toUpperCase()}</span>{(nota.obra.municipio || nota.obra.uf) && <span className="text-gray-600"> — {[nota.obra.municipio, nota.obra.uf].filter(Boolean).join('/').toUpperCase()}</span>}</>
                                                                : <span className="text-amber-800">Nota sem obra cadastrada</span>}
                                                        </div>
                                                        <div className="text-xs text-gray-600 mt-0.5 truncate">
                                                            {(nota.fornecedor.nome || '').toUpperCase()}{nota.fornecedor.cpfCnpj ? ` · ${maskCpfCnpj(nota.fornecedor.cpfCnpj)}` : ''}
                                                            {nota.boletosVinculados > 0 && ` · já tem ${nota.boletosVinculados} boleto(s) vinculado(s)`}
                                                        </div>
                                                        {nota.indicios.length > 0 && (
                                                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                                                                {nota.indicios.map((i) => {
                                                                    const { texto, classe } = descreverIndicio(i);
                                                                    return <span key={i} className={`text-xs px-2 py-0.5 rounded ${classe}`}>{texto}</span>;
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="text-sm font-semibold text-gray-800 whitespace-nowrap">{formatCurrencyFromDb(nota.valor)}</div>
                                                </div>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </div>

                <div className="px-4 sm:px-6 py-3 border-t flex flex-wrap items-center justify-end gap-2">
                    {selecionada && !selecionada.obra && !erroPeriodo && (
                        <span className="text-sm text-amber-800 mr-auto">Essa nota não tem obra: o boleto continuará sem obra no relatório.</span>
                    )}
                    <button type="button" onClick={onFechar} disabled={vinculando} className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50">
                        Cancelar
                    </button>
                    <button
                        type="button"
                        onClick={vincular}
                        disabled={!selecionada || vinculando || !!erroPeriodo}
                        className="px-4 py-2 rounded bg-stone-600 text-white hover:bg-stone-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
                    >
                        {vinculando ? 'Vinculando...' : notaAtual ? 'Substituir pela nota selecionada' : 'Vincular nota selecionada'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ModalVincularNotaBoleto;
